import React, { useEffect, useRef, useState } from 'react'
import { Accordion, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import Icon from '@mui/material/Icon'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ChargingScheduleChart, IFButton, IFDialog, IFText } from 'Components'
import Colors from 'Theme/Colors'
import PropTypes from 'prop-types'
import { formatChargingSchedulePeriods } from '../../Utils/Format'
import RequestState from 'Enums/RequestState'
import { styled } from '@mui/system'
import Styles from './ChargingProfileListItem.module.css'

const AccordionContainer = styled(Accordion)(({ theme }) => ({
  boxShadow: '0px 0px 0px 0px',
  borderRadius: '5px !important',
  width: '100%',
}))

const AccordionSummaryStyled = styled(MuiAccordionSummary)(({ theme }) => ({
  borderRadius: '5px',
  padding: '8px',
  '&:hover': {
    backgroundColor: Colors.ChargePointLogListItemHover,
  },
  '& .MuiAccordionSummary-content': {
    margin: '0px',
  },
}))

const AccordionSummaryContent = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
})

const AccordionDetailsContainer = styled('div')({
  padding: '16px',
})

const ChargingProfileListItem = ({
  name,
  maxCurrent,
  chargingSchedulePeriods,
  chargingSchedulePeriodsOffset,
  clearChargingProfile,
  clearChargePointChargingProfileRequestState,
  hideIndividualAxis,
  useAccordion = true,
}) => {
  const [chargingPeriods, setChargingPeriods] = useState([])
  const clearChargingProfileRef = useRef()
  const { t } = useTranslation()

  useEffect(() => {
    const formattedChargingPeriods = formatChargingSchedulePeriods(
      chargingSchedulePeriods,
      chargingSchedulePeriodsOffset,
    )
    setChargingPeriods(formattedChargingPeriods)
  }, [chargingSchedulePeriods])

  return (
    <div className={Styles.Container}>
      {useAccordion ? (
        <AccordionContainer>
          <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
            <AccordionSummaryContent>
              <div>
                <IFText>{name}</IFText>
              </div>
              <div>
                <IFText>{`${t(
                  'ChargingProfile.MaxCurrent',
                )}: ${maxCurrent} A`}</IFText>
              </div>
            </AccordionSummaryContent>
          </AccordionSummaryStyled>
          <AccordionDetails>
            <AccordionDetailsContainer>
              <div>
                {chargingPeriods.map((schedule, index) => (
                  <div
                    key={index}
                    className={index !== 0 ? Styles.AddTopMargin : {}}
                  >
                    <ChargingScheduleChart
                      day={schedule.day}
                      seriesData={schedule.periods}
                      chartHeight={50}
                      hideAxis={hideIndividualAxis}
                    />
                  </div>
                ))}
              </div>
              {hideIndividualAxis && (
                <div className={Styles.BottomContainer}>
                  <div className={Styles.EmptyDiv} />
                  <div className={Styles.LabelsContainer}>
                    <IFText className={Styles.LabelWidth}>12:00 AM</IFText>
                    <div className={Styles.LabelWidth}>
                      <IFText className={Styles.LabelMargin}>06:00 AM</IFText>
                    </div>
                    <div className={Styles.LabelWidth}>
                      <IFText className={Styles.LabelMargin}>12:00 PM</IFText>
                    </div>
                    <div className={Styles.LastLabelContainer}>
                      <IFText className={Styles.LabelMargin}>06:00 PM</IFText>
                      <IFText style={{ marginTop: 0 }}>11:59 PM</IFText>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <IFButton
                  color={Colors.primary}
                  isFill={true}
                  text={t('ChargingProfile.ClearProfile')}
                  onClick={() => clearChargingProfileRef.current.show()}
                  size="xsm"
                  isLoading={false}
                />
              </div>
            </AccordionDetailsContainer>
          </AccordionDetails>
          <IFDialog
            ref={clearChargingProfileRef}
            title={t('ChargingProfile.RemoveChargingProfileTitle')}
            subtitle={t('ChargingProfile.RemoveChargingProfileSubtitle')}
            onAction={clearChargingProfile}
            actionButtonIcon={
              <Icon
                sx={{ width: '18px !important', height: '18px !important' }}
                component={DeleteOutlineIcon}
              />
            }
            actionButtonText={t('ChargingProfile.ActionButton')}
            actionButtonColor={Colors.red}
            isLoading={
              clearChargePointChargingProfileRequestState ===
              RequestState.LOADING
            }
          />
        </AccordionContainer>
      ) : (
        <div className={Styles.AccordionDetailsContainer}>
          <div className={Styles.ChargingScheduleContainer}>
            {chargingPeriods.map((schedule, index) => (
              <div
                key={index}
                className={index !== 0 ? Styles.AddTopMargin : {}}
              >
                <ChargingScheduleChart
                  day={schedule.day}
                  seriesData={schedule.periods}
                  chartHeight={50}
                  hideAxis={hideIndividualAxis}
                />
              </div>
            ))}
          </div>
          {hideIndividualAxis && (
            <div className={Styles.BottomContainer}>
              <div className={Styles.EmptyDiv} />
              <div className={Styles.LabelsContainer}>
                <IFText className={Styles.LabelWidth}>12:00 AM</IFText>
                <div className={Styles.LabelWidth}>
                  <IFText className={Styles.LabelMargin}>06:00 AM</IFText>
                </div>
                <div className={Styles.LabelWidth}>
                  <IFText className={Styles.LabelMargin}>12:00 PM</IFText>
                </div>
                <div className={Styles.LastLabelContainer}>
                  <IFText className={Styles.LabelMargin}>06:00 PM</IFText>
                  <IFText style={{ marginTop: 0 }}>11:59 PM</IFText>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ChargingProfileListItem.propTypes = {
  name: PropTypes.string,
  maxCurrent: PropTypes.number,
  chargingSchedulePeriods: PropTypes.arrayOf(PropTypes.object),
  chargingSchedulePeriodsOffset: PropTypes.number,
  clearChargingProfile: PropTypes.func,
  clearChargePointChargingProfileRequestState: PropTypes.number,
  hideIndividualAxis: PropTypes.bool,
}

export default ChargingProfileListItem
